import React, { useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Collapse, Group, Paper, Select, Stack, Table, Text, TextInput, Textarea, Title, Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronRight, IconColumnsOff, IconInfoCircle, IconRefresh, IconTable } from '@tabler/icons-react';
import SourceSamplePanel from './SourceSamplePanel';
import { CategoryMoveNotice, ContactNameNotice, MixedContactsNotice, ProblemInline, RetiredTargetNotice, SavedRecipeNotice } from './RecipeNotices';
import {
  ColumnProfile, ImportDraft, ImportRecipe, TRANSFORMS_WITH_ARGUMENT, TRANSFORM_KINDS, TRANSFORM_LABELS, TargetFieldInfo, TransformKind,
} from '@/services/import-draft/import-draft-model';
import {
  LOW_CONFIDENCE, blockingProblems, canContinueFromMapping, canIgnoreProblem, cleaningCount, cleaningExamples, columnLabel, columnSamples, confidenceTone, findMapping, formatTokens,
  generalWarnings, ignoredColumnsWithData, ignoredReason, isSavedRecipe, lowConfidenceMappings, problemsForTarget, recipeSourceLabel,
  mapBackToType, setFieldArgument, setFieldSource, setFieldTransform, unmappedColumns, unmappedRequired,
} from '@/services/import-draft/import-draft-helpers';

interface Props {
  draft: ImportDraft;
  recipe: ImportRecipe;
  dirty: boolean;
  onRecipeChange: (recipe: ImportRecipe) => void;
  onContinue: () => void;
  onReinfer: (instruction: string) => void;
  busy: boolean;
}

const NOT_IMPORTED = '__none__';

export default function MappingStep({ draft, recipe, dirty, onRecipeChange, onContinue, onReinfer, busy }: Props) {
  const [instruction, setInstruction] = useState(draft.UserInstruction || '');
  const [showIgnored, setShowIgnored] = useState(false);
  const [showOptional, setShowOptional] = useState(true);
  const [showSample, setShowSample] = useState(false);
  const [editingTransform, setEditingTransform] = useState<Record<string, boolean>>({});

  const profiles = draft.Profiles || [];
  const columnOptions = useMemo(() => [
    { value: NOT_IMPORTED, label: 'Not imported' },
    ...profiles
      .filter(p => p.Header || p.NonEmptyCount > 0)
      .map(p => ({ value: String(p.Index), label: columnLabel(profiles, p.Index) })),
  ], [profiles]);

  const missingRequired = unmappedRequired(recipe, draft.TargetFields);
  const shaky = lowConfidenceMappings(recipe);
  const ignored = unmappedColumns(recipe, profiles);
  const ignoredWithData = ignoredColumnsWithData(recipe, profiles);
  const dataIndexes = new Set(ignoredWithData.map(p => p.Index));
  const otherIgnored = ignored.filter(p => !dataIndexes.has(p.Index));
  const errors = blockingProblems(draft.Problems);
  const notes = [...(recipe.Warnings || []), ...generalWarnings(draft.Problems).map(p => p.Message)];
  const canContinue = canContinueFromMapping(draft, recipe) && errors.length === 0;
  const tokens = formatTokens(draft);
  const unmappedTargets = draft.TargetFields.filter(f => !findMapping(recipe, f.Name)).map(f => ({ value: f.Name, label: f.Name }));

  const mappedCount = recipe.Fields.length;
  const orderedFields = [...draft.TargetFields].sort((a, b) => {
    const am = findMapping(recipe, a.Name) ? 0 : 1;
    const bm = findMapping(recipe, b.Name) ? 0 : 1;
    if (a.Required !== b.Required) return a.Required ? -1 : 1;
    return am - bm;
  });
  const visibleFields = showOptional ? orderedFields : orderedFields.filter(f => f.Required || findMapping(recipe, f.Name));

  function renderRow(field: TargetFieldInfo) {
    const mapping = findMapping(recipe, field.Name);
    const source = mapping?.Sources?.[0];
    const argumentLabel = mapping ? TRANSFORMS_WITH_ARGUMENT[mapping.Transform] : undefined;
    const samples = source !== undefined ? columnSamples(profiles, source, 3) : [];
    const editing = !!editingTransform[field.Name];
    const rowProblems = mapping ? problemsForTarget(draft.Problems, field.Name) : [];

    return (
      <Table.Tr key={field.Name} data-testid={`mapping-row-${field.Name}`}>
        <Table.Td>
          <Group gap={6}>
            <Text size="sm" fw={500}>{field.Name}</Text>
            {field.Required && <Badge size="xs" color="red" variant="light">required</Badge>}
          </Group>
          <Text size="xs" c="dimmed">{field.Description}</Text>
          {field.AllowedValues && field.AllowedValues.length > 0 && (
            <Text size="xs" c="dimmed">Allowed: {field.AllowedValues.join(', ')}</Text>
          )}
        </Table.Td>
        <Table.Td>
          <Select
            data={columnOptions}
            value={mapping && mapping.Transform !== 'Constant' && source !== undefined ? String(source) : NOT_IMPORTED}
            onChange={value => onRecipeChange(setFieldSource(recipe, field.Name, value && value !== NOT_IMPORTED ? Number(value) : null))}
            searchable
            allowDeselect={false}
            size="xs"
            disabled={busy}
            aria-label={`Source column for ${field.Name}`}
          />
          {samples.length > 0 && (
            <Text size="xs" c="dimmed" mt={4} lineClamp={2} title={samples.join(' | ')} data-testid={`samples-${field.Name}`}>
              e.g. {samples.join(' · ')}
            </Text>
          )}
          {rowProblems.map((p, i) => (
            <ProblemInline
              key={i}
              message={p.Message}
              busy={busy}
              onIgnore={canIgnoreProblem(p, field.Name) ? () => onRecipeChange(setFieldSource(recipe, field.Name, null)) : undefined}
            />
          ))}
        </Table.Td>
        <Table.Td>
          {mapping && (
            <Stack gap={4}>
              {editing ? (
                <Select
                  data={TRANSFORM_KINDS.map(k => ({ value: k, label: TRANSFORM_LABELS[k] }))}
                  value={mapping.Transform}
                  onChange={value => value && onRecipeChange(setFieldTransform(recipe, field.Name, value as TransformKind, mapping.Argument))}
                  size="xs"
                  allowDeselect={false}
                  disabled={busy}
                  aria-label={`Transform for ${field.Name}`}
                />
              ) : (
                <Group gap={4} wrap="nowrap">
                  <Badge size="sm" variant="light" color={mapping.Transform === 'Copy' ? 'gray' : 'blue'} data-testid={`transform-${field.Name}`} style={{ textTransform: 'none' }}>
                    {TRANSFORM_LABELS[mapping.Transform]}{mapping.Argument ? ` (${mapping.Argument})` : ''}
                  </Badge>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() => setEditingTransform(v => ({ ...v, [field.Name]: true }))}
                    disabled={busy}
                    aria-label={`Change format for ${field.Name}`}
                  >
                    Change
                  </Button>
                </Group>
              )}
              {editing && argumentLabel && (
                <TextInput
                  size="xs"
                  placeholder={argumentLabel}
                  value={mapping.Argument || ''}
                  onChange={e => onRecipeChange(setFieldArgument(recipe, field.Name, e.currentTarget.value))}
                  disabled={busy}
                  aria-label={`${argumentLabel} for ${field.Name}`}
                />
              )}
              {mapping.Transform === 'AiClean' && (() => {
                const count = cleaningCount(recipe, field.Name);
                const examples = cleaningExamples(recipe, field.Name);
                if (count === null) return <Text size="xs" c="dimmed">These values will be cleaned up when you continue.</Text>;
                return (
                  <Stack gap={0} data-testid={`cleaning-${field.Name}`}>
                    <Text size="xs" c="dimmed">{count} value{count === 1 ? '' : 's'} corrected</Text>
                    {examples.map(e => <Text size="xs" c="dimmed" key={e.From} lineClamp={1} title={`${e.From} → ${e.To}`}>{e.From} → {e.To || '(blank)'}</Text>)}
                  </Stack>
                );
              })()}
            </Stack>
          )}
        </Table.Td>
        <Table.Td>
          {mapping && (
            <Stack gap={2}>
              <Tooltip label={mapping.Reason ? `${(mapping.Confidence * 100).toFixed(0)}%: ${mapping.Reason}` : `Confidence ${(mapping.Confidence * 100).toFixed(0)}%`} multiline w={280}>
                <Badge size="sm" color={confidenceTone(mapping.Confidence)} variant="dot">
                  {mapping.Confidence >= 0.9 ? 'High' : mapping.Confidence >= LOW_CONFIDENCE ? 'Medium' : 'Low'}
                </Badge>
              </Tooltip>
              {mapping.Confidence < LOW_CONFIDENCE && mapping.Reason && (
                <Text size="xs" c="red.8" lineClamp={2} title={mapping.Reason} data-testid={`reason-${field.Name}`}>{mapping.Reason}</Text>
              )}
            </Stack>
          )}
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Stack gap="md" data-testid="mapping-step">
      <div>
        <Title order={4}>Check the column mapping</Title>
        <Text size="sm" c="dimmed">
          {recipeSourceLabel(draft.RecipeSource, draft.SourceLabel)}{tokens ? ` · ${tokens}` : ''}. Check that each ServCraft field takes the right column, then press Next.
        </Text>
      </div>

      {isSavedRecipe(draft) && <SavedRecipeNotice draft={draft} busy={busy} onRedetect={() => onReinfer(instruction)} />}

      {errors.length > 0 && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Resolve these problems before continuing">
          {errors.map((p, i) => <Text size="sm" key={i}>{p.Target ? `${p.Target}: ` : ''}{p.Message}</Text>)}
        </Alert>
      )}

      <ContactNameNotice draft={draft} recipe={recipe} onRecipeChange={onRecipeChange} busy={busy} />
      <MixedContactsNotice draft={draft} />
      <CategoryMoveNotice draft={draft} recipe={recipe} busy={busy} onUndo={columnIndex => onRecipeChange(mapBackToType(recipe, columnIndex))} />
      <RetiredTargetNotice draft={draft} />

      {ignoredWithData.length > 0 && (
        <Paper withBorder radius="md" p="sm" bg="yellow.0" data-testid="ignored-with-data">
          <Group gap="xs" mb="xs">
            <IconColumnsOff size={16} />
            <Text size="sm" fw={600}>
              {ignoredWithData.length} column{ignoredWithData.length === 1 ? ' has' : 's have'} data but will not be imported
            </Text>
          </Group>
          <Stack gap="xs">
            {ignoredWithData.map(p => (
              <IgnoredColumnRow
                key={p.Index}
                profile={p}
                label={columnLabel(profiles, p.Index)}
                reason={ignoredReason(recipe, p.Index)}
                targets={unmappedTargets}
                busy={busy}
                onMap={target => onRecipeChange(setFieldSource(recipe, target, p.Index))}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {notes.length > 0 && (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />} title="Notes">
          {notes.map((w, i) => <Text size="sm" key={i}>• {w}</Text>)}
        </Alert>
      )}

      <div>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconTable size={14} />}
          rightSection={showSample ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          onClick={() => setShowSample(v => !v)}
        >
          {showSample ? 'Hide file preview' : 'Preview the file'}
        </Button>
        <Collapse in={showSample} mt="xs">
          {showSample && <SourceSamplePanel draftID={draft.ID} recipe={recipe} />}
        </Collapse>
      </div>

      <Table verticalSpacing="xs" withTableBorder withColumnBorders={false} striped styles={{ td: { verticalAlign: 'top' } }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w="26%">ServCraft field</Table.Th>
            <Table.Th w="32%">Column in file</Table.Th>
            <Table.Th w="22%">Format</Table.Th>
            <Table.Th>Confidence</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{visibleFields.map(renderRow)}</Table.Tbody>
      </Table>

      <Group justify="space-between">
        <Button variant="subtle" size="xs" onClick={() => setShowOptional(v => !v)} leftSection={showOptional ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}>
          {showOptional ? 'Hide unmapped optional fields' : 'Show all fields'}
        </Button>
        {otherIgnored.length > 0 && (
          <Button variant="subtle" size="xs" onClick={() => setShowIgnored(v => !v)} leftSection={showIgnored ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}>
            {otherIgnored.length} mostly empty column{otherIgnored.length === 1 ? '' : 's'} will not be imported
          </Button>
        )}
      </Group>

      <Collapse in={showIgnored}>
        <Table verticalSpacing="xs" withTableBorder>
          <Table.Tbody>
            {otherIgnored.map(p => (
              <Table.Tr key={p.Index}>
                <Table.Td w="30%"><Text size="sm" fw={500}>{columnLabel(profiles, p.Index)}</Text><Text size="xs" c="dimmed">{ignoredReason(recipe, p.Index) || 'Not mapped'}</Text></Table.Td>
                <Table.Td w="40%"><Text size="xs" c="dimmed" lineClamp={2}>{p.Samples.join(' · ')}</Text></Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    placeholder="Map to…"
                    data={unmappedTargets}
                    onChange={value => value && onRecipeChange(setFieldSource(recipe, value, p.Index))}
                    searchable
                    disabled={busy}
                    aria-label={`Map ${columnLabel(profiles, p.Index)} to a field`}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Collapse>

      <Stack gap="xs">
        <Textarea
          label="Add instructions and remap"
          placeholder='For example: "The County column contains the country" or "Skip rows where Type is Supplier"'
          value={instruction}
          onChange={e => setInstruction(e.currentTarget.value)}
          autosize
          minRows={1}
          disabled={busy}
        />
        <Group>
          <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={() => onReinfer(instruction)} disabled={busy}>
            Remap columns
          </Button>
          <Text size="xs" c="dimmed">Remapping replaces any changes you have made.</Text>
        </Group>
      </Stack>

      <Group justify="space-between" mt="sm" style={{ position: 'sticky', bottom: 0, background: 'white', padding: '8px 0' }}>
        <Stack gap={0}>
          <Text size="sm">
            {mappedCount} of {draft.TargetFields.length} fields mapped · {ignored.length} columns ignored
            {shaky.length > 0 ? ` · ${shaky.length} to check` : ''}
            {dirty ? ' · unsaved changes' : ''}
          </Text>
          {missingRequired.length > 0 && (
            <Text size="xs" c="red">Required fields not mapped: {missingRequired.map(f => f.Name).join(', ')}</Text>
          )}
        </Stack>
        <Button onClick={onContinue} disabled={!canContinue} loading={busy}>
          {dirty ? 'Save and continue' : 'Next'}
        </Button>
      </Group>
    </Stack>
  );
}

interface IgnoredColumnRowProps {
  profile: ColumnProfile;
  label: string;
  reason: string | null;
  targets: { value: string; label: string }[];
  busy: boolean;
  onMap: (target: string) => void;
}

function IgnoredColumnRow({ profile, label, reason, targets, busy, onMap }: IgnoredColumnRowProps) {
  const [choosing, setChoosing] = useState(false);
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" data-testid={`ignored-column-${profile.Index}`}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Text size="sm" fw={500}>{label}</Text>
        <Text size="xs" c="dimmed">{reason || 'The column was not matched to a ServCraft field.'}</Text>
        {profile.Samples.length > 0 && (
          <Text size="xs" c="dimmed" lineClamp={1} title={profile.Samples.join(' | ')}>e.g. {profile.Samples.slice(0, 3).join(' · ')}</Text>
        )}
      </div>
      {choosing ? (
        <Select
          size="xs"
          placeholder="Choose a field"
          data={targets}
          onChange={value => value && onMap(value)}
          searchable
          defaultDropdownOpened
          disabled={busy}
          aria-label={`Map ${label} to a field`}
          w={220}
        />
      ) : (
        <Button size="xs" variant="light" onClick={() => setChoosing(true)} disabled={busy || targets.length === 0}>Map this</Button>
      )}
    </Group>
  );
}
